import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { useTheme } from '@mui/material'
import { humanizeTimestamp, moreThanMinutesAgo } from '../utils/date'
import { NotificationsActive } from '@mui/icons-material'
import BlinkingComponent from './BlinkingComponent'
import { NavLink } from 'react-router-dom'
import { RuuviTag } from '../types'

interface RuuviTagStatsTextProps {
  value: number
  decimals?: number
  unit: string
  alerting?: boolean
}

function RuuviTagStatsText({
  value,
  decimals = 2,
  unit,
  alerting = false
}: RuuviTagStatsTextProps) {
  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The value typography color.
   */
  const valueTypographyColor = alerting ? theme.palette.error.main : theme.palette.text.primary

  /**
   * The unit typography color.
   */
  const unitTypographyColor = alerting ? theme.palette.error.main : theme.palette.text.secondary

  return (
    <Stack direction="column">
      <Typography variant="h3" color={valueTypographyColor}>
        {value.toFixed(decimals)}
      </Typography>
      <Typography variant="h6" color={unitTypographyColor}>
        {unit}
      </Typography>
    </Stack>
  )
}

interface RuuviTagStatsProps {
  ruuviTag?: RuuviTag
  title: string | ReactNode
  value?: number
  decimals?: number
  unit?: string
  date?: string
  isAging?: boolean
  loading?: boolean
  alerting?: boolean
  alertText?: string
}

export default function RuuviTagStats({
  ruuviTag,
  title,
  value,
  decimals = 2,
  unit,
  date,
  isAging = false,
  loading = false,
  alerting = false,
  alertText
}: RuuviTagStatsProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * Check if the date is outdated.
   */
  const isOutdated = isAging && date && moreThanMinutesAgo(new Date(date), 60)

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title={title}
        action={
          <Stack direction="row" alignItems="center" gap={2}>
            {alerting && (
              <Tooltip title={alertText}>
                <BlinkingComponent>
                  <IconButton
                    color="error"
                    component={NavLink}
                    to={`/ruuvi-tags/${ruuviTag?.id}/alerts`}
                    size="small"
                  >
                    <NotificationsActive fontSize="inherit" />
                  </IconButton>
                </BlinkingComponent>
              </Tooltip>
            )}

            {loading ? (
              <Skeleton animation="wave" variant="text" width="100px" height="24px" />
            ) : (
              date &&
              !isOutdated && (
                <Typography sx={{ color: theme.palette.text.secondary }}>
                  {humanizeTimestamp(date)}
                </Typography>
              )
            )}
          </Stack>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {loading ? (
          <Stack direction="column" gap={0}>
            <Skeleton
              animation="wave"
              variant="text"
              width="64px"
              sx={{ display: 'block', fontSize: '1.5rem' }}
            />

            <Skeleton
              animation="wave"
              variant="text"
              width="24px"
              sx={{ display: 'block', fontSize: '1rem' }}
            />
          </Stack>
        ) : value === undefined ? (
          <Alert variant="outlined" severity="warning" sx={{ p: 0, border: 0 }}>
            {t('readingIsMissing')}
          </Alert>
        ) : date && isOutdated ? (
          <Tooltip
            title={
              <Stack direction="column" gap={1}>
                <Box>{humanizeTimestamp(date)}</Box>
                <Box>{`${value.toFixed(decimals)} ${unit}`}</Box>
              </Stack>
            }
          >
            <Alert variant="outlined" severity="warning" sx={{ p: 0, border: 0 }}>
              {t('readingIsOutdated')}
            </Alert>
          </Tooltip>
        ) : alerting ? (
          <BlinkingComponent>
            <RuuviTagStatsText
              value={value}
              decimals={decimals}
              unit={unit || ''}
              alerting={alerting}
            />
          </BlinkingComponent>
        ) : (
          <RuuviTagStatsText value={value} decimals={decimals} unit={unit || ''} />
        )}
      </CardContent>
    </Card>
  )
}
